import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
const LIKELIHOODS = ['Low', 'Possible', 'Likely'];
const SEVERITIES = ['Minor injury', 'Serious injury', 'Fatality potential'];
// Simple risk matrix logic for demo
const getRiskLevel = (l: string, s: string) => {
  if (s === 'Fatality potential' || s === 'Serious injury' && l === 'Likely') return 'HIGH';
  if (l === 'Low' && s === 'Minor injury') return 'LOW';
  return 'MEDIUM';
};
export function RiskEvaluationScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  // We're just showing one example assessment for the prototype
  const assessment = data.riskAssessments[0];
  const [likelihood, setLikelihood] = useState(assessment.likelihood);
  const [severity, setSeverity] = useState(assessment.severity);
  const currentRisk = getRiskLevel(likelihood, severity);
  const handleContinue = () => {
    const updated = [...data.riskAssessments];
    updated[0] = {
      ...updated[0],
      likelihood,
      severity,
      riskLevel: currentRisk as any
    };
    updateData({
      riskAssessments: updated
    });
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-306">
      <div className="mb-6" data-id="element-307">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-308">Risk Assessment</h2>
        <p className="text-slate-400" data-id="element-309">
          Evaluate the risk for identified hazards.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2" data-id="element-310">
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-8 flex items-start gap-3" data-id="element-311">
          <AlertTriangleIcon className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-312" />
          <div data-id="element-313">
            <p className="text-xs text-orange-400 uppercase tracking-wider font-bold mb-1" data-id="element-314">
              Evaluating Hazard
            </p>
            <p className="text-white font-medium text-lg" data-id="element-315">
              {assessment.hazard}
            </p>
          </div>
        </div>

        <div className="space-y-8" data-id="element-316">
          {/* Likelihood */}
          <div data-id="element-317">
            <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-318">
              Likelihood
            </p>
            <div className="grid grid-cols-3 gap-2" data-id="element-319">
              {LIKELIHOODS.map(l => <button key={l} onClick={() => setLikelihood(l)} className={`py-3 px-2 rounded-lg text-sm font-medium transition-colors border-2 ${likelihood === l ? 'bg-slate-800 border-orange-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-320">
                  {l}
                </button>)}
            </div>
          </div>

          {/* Severity */}
          <div data-id="element-321">
            <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-322">
              Severity
            </p>
            <div className="flex flex-col gap-2" data-id="element-323">
              {SEVERITIES.map(s => <button key={s} onClick={() => setSeverity(s)} className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors border-2 text-left ${severity === s ? 'bg-slate-800 border-orange-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-324">
                  {s}
                </button>)}
            </div>
          </div>

          {/* Risk Calculation Display */}
          <div className="mt-8 pt-8 border-t border-slate-800 flex items-center justify-between" data-id="element-325">
            <div data-id="element-326">
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1" data-id="element-327">
                Calculated Risk
              </p>
              <motion.div key={currentRisk} initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className={`text-4xl font-black tracking-tight ${currentRisk === 'HIGH' ? 'text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]' : currentRisk === 'MEDIUM' ? 'text-amber-500 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]' : 'text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]'}`} data-id="element-328">
                {currentRisk}
              </motion.div>
            </div>

            {/* Mini Matrix Visual */}
            <div className="w-16 h-16 grid grid-cols-3 grid-rows-3 gap-0.5 bg-slate-800 p-0.5 rounded" data-id="element-329">
              {/* Row 1 (Fatality) */}
              <div className={`bg-amber-500/20 ${severity === 'Fatality potential' && likelihood === 'Low' ? 'bg-amber-500' : ''}`} data-id="element-330" />
              <div className={`bg-red-500/20 ${severity === 'Fatality potential' && likelihood === 'Possible' ? 'bg-red-500' : ''}`} data-id="element-331" />
              <div className={`bg-red-500/20 ${severity === 'Fatality potential' && likelihood === 'Likely' ? 'bg-red-500' : ''}`} data-id="element-332" />
              {/* Row 2 (Serious) */}
              <div className={`bg-green-500/20 ${severity === 'Serious injury' && likelihood === 'Low' ? 'bg-green-500' : ''}`} data-id="element-333" />
              <div className={`bg-amber-500/20 ${severity === 'Serious injury' && likelihood === 'Possible' ? 'bg-amber-500' : ''}`} data-id="element-334" />
              <div className={`bg-red-500/20 ${severity === 'Serious injury' && likelihood === 'Likely' ? 'bg-red-500' : ''}`} data-id="element-335" />
              {/* Row 3 (Minor) */}
              <div className={`bg-green-500/20 ${severity === 'Minor injury' && likelihood === 'Low' ? 'bg-green-500' : ''}`} data-id="element-336" />
              <div className={`bg-green-500/20 ${severity === 'Minor injury' && likelihood === 'Possible' ? 'bg-green-500' : ''}`} data-id="element-337" />
              <div className={`bg-amber-500/20 ${severity === 'Minor injury' && likelihood === 'Likely' ? 'bg-amber-500' : ''}`} data-id="element-338" />
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4" data-id="element-339">
        <PrimaryButton onClick={handleContinue} data-id="element-340">Continue</PrimaryButton>
      </div>
    </div>;
}